import { useCallback, useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";

const CACHE_TTL_MS = 30_000;

let _sharedCache = { data: null, promise: null, fetchedAt: 0, error: null };

async function fetchCapabilitiesShared() {
  const now = Date.now();
  if (_sharedCache.data && now - _sharedCache.fetchedAt < CACHE_TTL_MS) {
    return _sharedCache.data;
  }
  if (!_sharedCache.promise) {
    _sharedCache.promise = api
      .get("/auth/capabilities", { timeout: 60000 })
      .then(({ data }) => {
        _sharedCache.data = data;
        _sharedCache.fetchedAt = Date.now();
        _sharedCache.error = null;
        _sharedCache.promise = null;
        return data;
      })
      .catch((err) => {
        _sharedCache.promise = null;
        _sharedCache.error = err;
        return null;
      });
  }
  return _sharedCache.promise;
}

/** Invalida caché tras login, licencia o cambios de permisos. */
export function invalidateCapabilitiesCache() {
  _sharedCache = { data: null, promise: null, fetchedAt: 0, error: null };
}

/** Lectura síncrona para adaptadores legacy (TODO FASE 3). */
export function getCachedCapabilities() {
  return _sharedCache.data;
}

/**
 * Hook centralizado Fase 2 — GET /auth/capabilities.
 */
export function useCapabilities() {
  const { user } = useAuth();
  const [data, setData] = useState(_sharedCache.data);
  const [loading, setLoading] = useState(!_sharedCache.data);
  const [error, setError] = useState(_sharedCache.error);

  const reload = useCallback(async () => {
    invalidateCapabilitiesCache();
    setLoading(true);
    const fresh = await fetchCapabilitiesShared();
    setData(fresh);
    setError(fresh ? null : _sharedCache.error);
    setLoading(false);
    return fresh;
  }, []);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    fetchCapabilitiesShared().then((fresh) => {
      if (!cancelled) {
        setData(fresh);
        setError(fresh ? null : _sharedCache.error);
        setLoading(false);
      }
    });
    return () => {
      cancelled = true;
    };
  }, [user?.id]);

  const state = useMemo(() => {
    const platform = data?.platform || {};
    const subscription = data?.subscription || {};
    const permissions = data?.permissions || {};
    return {
      data,
      loading,
      error,
      role: data?.role || null,
      modules: data?.modules || {},
      permissions,
      subscription,
      isPlatformDesigner: Boolean(platform.is_platform_designer),
      allModules: Boolean(platform.all_modules),
      schemaVersion: data?.schema_version ?? null,
      resolvedAt: data?.resolved_at || null,
      compat: data?._compat || null,
    };
  }, [data, loading, error]);

  return {
    ...state,
    reload,
  };
}
